import React, { useCallback, useContext, useMemo, useState } from 'react'
import { BulkCertifyParam, certificationApi } from '../../../utils/api/certification'
import { useQuery } from 'react-query'
import {
  getCertLineDateBounds,
  getCertLineDefaultDate,
  resolveProjectTimezone,
} from '../../../utils/certDefaultDate';
import { map as bluebirdMap } from "bluebird";
import { FormContext, FormContextProvider, FormContextValue, FormSchemaFields } from '@stormwater-software/lib-form'
import { FormModal } from '../../Shared/form'
import { CertFormSchema, CertSchemaField, CertificationIndexResponse, SignatureGroupType, fieldKeyCodec, getCertFormDateData, getCertFormDateSchema, getCertFormUserData, getCertFormUserSchema } from '@stormwater-software/lib-certification';
import { find, get } from 'lodash';
import { getInspectionTypeLabel } from '../../../utils';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import InspectionCertificationStatement from './InspectionCertificationStatement'
import SignatureCheckbox from './SignatureCheckbox';
import AppContext from '../../../contexts/AppContext';
import DashboardContext from '../../../contexts/DashboardContext';
import {
  pendingPhotoCount,
  usePendingPhotoUploads,
} from '../../../hooks/backgroundUploads';
import { useOfflineSignBlock } from '../../../hooks/useOfflineSignBlock';


function extractSchemaData(
    formData: Record<string, any>,
    state: { id: number; certState: CertificationIndexResponse }[],
  ): BulkCertifyParam {
    return state.reduce<BulkCertifyParam>((data, { id, certState }) => {
      certState.signatureGroups[SignatureGroupType.acknowledgement].forEach(
        ({ type: lineType }) => {
          const key = fieldKeyCodec.encode(lineType, CertSchemaField.date, id);
          const date = get(formData, key, null);
  
          if (date) {
            data.push({ id, date, lineType });
          }
        },
      );
  
      return data;
    }, []);
}

function getLabelSchema(label: string, isHeading?: boolean) {
    return {
      label,
      controlType: UIControlType.plainText,
      className: isHeading ? "modal-h3" : undefined,
    };
}

const BulkAcknowledgementCertifyModal = ({
    inspections,
    hideModal,
    handleSaveAnimation,
}:{
    inspections: any,
    hideModal: () => void;
    handleSaveAnimation: () => void;
}) => {
  const [checked, setChecked] = useState(false);
  const [inspectionName, setInspectionName] = useState<any[]>([])
  const appContext = useContext(AppContext);
  const user = appContext.get("user");
  const { fetchFuncForAcknowledgementCert } = useContext(DashboardContext);
  const [error, setError] = useState(false)
  const inspectionIds = inspections.map((_:any) => _.id);
  // SW2-2458: signing builds and emails the report, so it waits for every finding
  // photo still uploading for these inspections. Enforced at the checkbox and at
  // submit, and live, so it lifts by itself when the uploads finish.
  const uploadingPhotos = pendingPhotoCount(
    usePendingPhotoUploads({ kind: 'finding' }).filter((job) =>
      inspectionIds.includes(Number(job.inspectionId)),
    ),
  );
  const photoBlock = uploadingPhotos
    ? `Waiting for ${uploadingPhotos} photo${
        uploadingPhotos === 1 ? '' : 's'
      } to finish uploading before signing.`
    : null;
  // SW2-2459: nor while a downloaded project in the selection has offline work waiting — the
  // bulk endpoints have no queue, so the report would go out without it.
  const offlineBlock = useOfflineSignBlock(inspectionIds);
  const uploadBlock = photoBlock || offlineBlock;
  const [selectedLabel, setSelectedLabel] = useState<string>()
  const [allLabels, setAllLabels] = useState<string[]>([])
  const query = useQuery({
    queryKey: ["InspectionCert", ...inspectionIds],
    queryFn: () =>
      bluebirdMap(
        inspectionIds,
        async (id:any) => {
          return {
            id,
            certState: await certificationApi.index(id),
          };
        },
        { concurrency: 4 },
      ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const handleSubmit = useCallback(
    async (formData, formContext: FormContextValue) => {
      let updatedData = query.data

      if (uploadBlock) {
        formContext.setError(uploadBlock);

        return;
      }

      if (!checked) {
          formContext.setError(
              "Please complete the Electronic Authorization Signature",
          );

          return;
      }

      if(selectedLabel && updatedData){
        updatedData = updatedData.map((element) => {
          element.certState.signatureGroups.compliance = element.certState.signatureGroups.compliance.filter((ele:any) => {
            if(selectedLabel === "ALL Signatures"){
              return true
            }

            if(selectedLabel === "NO Label"){
              return (ele.label === null || ele.label === "")
            }

            return ele.label === selectedLabel
          })

          return element
        })
      }

      return certificationApi
      .bulkAcknowledgementCertify(extractSchemaData(formData, updatedData || []))
      .then(() => {
      //   onSubmit();
        fetchFuncForAcknowledgementCert.refetch()
        hideModal();
        handleSaveAnimation();
      });
    },
    [checked, uploadBlock, query.data],
  );

  /**
   * SW2-2239. Replaces a local `acknowledgementCertDefaultDate` that hand-rolled
   * `new Date(doi.getTime() + doi.getTimezoneOffset() * 60000)` and, for acknowledgement, read
   * `signatureGroups.acknowledgement[0].defaultDateLogic` — the FIRST line's setting applied to every line
   * in the group, though the column is per line.
   *
   * Also supplies the picker's `minDate` per line, from each inspection's own locked groups (SW2-2539).
   */
  const timezone = resolveProjectTimezone(
    (query.data as any)?.[0]?.certState?.projectTimezone,
  );

  const lineDateArgs = (certState: any, line: any) => ({
    group: 'acknowledgement' as const,
    defaultDateLogic: line.defaultDateLogic,
    inspectionDOI: certState.inspectionDOI,
    timezone,
    // SW2-2539: this inspection's own locks — a dashboard batch can mix clients.
    lockedGroups: certState.lockedCertGroups,
    isCDOT: certState.isCDOT,
  });

  const schema = useMemo(() => {
    const sch: CertFormSchema = {};
  
    if (query.data && query.data.length) {
      const firstGroups = query.data[0].certState.signatureGroups;
      const firstLine =
        firstGroups.acknowledgement.length > 0
          ? firstGroups.acknowledgement[0]
          : firstGroups.inspection.length > 0
            ? firstGroups.inspection[0]
            : undefined;

      if (firstLine !== undefined) {
        // name and position fields (first inspection & signature line only)
        Object.assign(
          sch,
          getCertFormUserSchema({
            encodeKey: (type, field) =>
              fieldKeyCodec.encode(type, field, inspectionIds[0]),
            line: firstLine,
          }),
        );
      }

      // date field, each inspection, each line
      query.data.forEach(({ certState, id }) => {
        const insp:any = find(inspections, { id });

        if (!certState.groupOptions[
          SignatureGroupType.acknowledgement
        ].enabled) {
          setError(true)
          const abc: any[] = inspectionName

          insp && abc.push(`${insp.created_date} | ${insp.type}`)
          setInspectionName(abc)
        }

        let lines = certState.signatureGroups.acknowledgement.filter(line => {
          return (
            find(certState.userSignatures.acknowledgement, {
              lineType: line.type,
            }) === undefined
          );
        });

        //adding all labels in the state for filtering based on Label
        lines.forEach(ele => {
          let displayLabel = ele.label

          if(ele.label === null || ele.label === ""){
            displayLabel = "NO Label"
          }

          setAllLabels(prevState => {
            if(prevState.includes(`${displayLabel}`)){
              return [...prevState]
            }
            else{
              return [...prevState, `${displayLabel}`]
            }
          })
        })
        
        if(selectedLabel){
          lines = lines.filter(ele => {
            if(selectedLabel === "ALL Signatures"){
              return true
            }

            if(selectedLabel === "NO Label"){
              return (ele.label === null || ele.label === "")
            }

            return ele.label === selectedLabel
          })
        }

        if (insp && lines.length) {
          Object.assign(sch, {
            [`${id}-label`]: getLabelSchema(getInspectionTypeLabel(insp, false), true),
          });

          lines.forEach(line => {
            const dateSchema = getCertFormDateSchema({
              enabled: true,
              line,
              // SW2-2239: per line, and timezone-corrected. Was
              // `new Date(certState.minCertificationDate)` — one value for the whole group with no
              // timezone handling, so the picker allowed one calendar day earlier than the server's
              // minimum in every US zone. `maxDate` is already pinned to today by lib-certification.
              minDate: getCertLineDateBounds(lineDateArgs(certState, line)).minDate,
              encodeKey: (type, field) => fieldKeyCodec.encode(type, field, id),
              label: line.label ? `${line.label} Date` : "Signature Date",
            });
            const [dateFieldKey] = Object.keys(dateSchema);

            Object.assign(dateSchema[dateFieldKey].style, {
              // flex: "0 1 20rem",
              flex: "0 1 100%",
              // minWidth: "20rem",
              // display: "flex",
              // flexFlow: "row nowrap",
            });

            Object.assign(dateSchema[dateFieldKey], {
              isClearable: true,
            });

            Object.assign(sch, dateSchema);
          });
        }
      });
    }

    return sch;
  }, [query.data, selectedLabel]);


  const initialValue = useMemo(() => {
    const data = {};

    if (query.data) {
      query.data.forEach(({ certState, id }) => {    
        if (
        //   certState.groupOptions.acknowledgement.enabled &&
          certState.signatureGroups.acknowledgement.length
        ) {
          let lines = certState.signatureGroups.acknowledgement.filter(line => {
          return (
            find(certState.userSignatures.acknowledgement, {
            lineType: line.type,
            }) === undefined
          );
          });

          lines.forEach(line => {
          Object.assign(
            data,
            getCertFormDateData({
              encodeKey: (type: string, field: CertSchemaField) =>
                fieldKeyCodec.encode(type, field, id),
              lineType: line.type,
              value: getCertLineDefaultDate(lineDateArgs(certState, line)),
            }),
            getCertFormUserData({
              encodeKey: (type: string, field: CertSchemaField) =>
                fieldKeyCodec.encode(type, field, id),
              lineType: line.type,
              value: {
                  name: user.name,
                  role: user.position || user.roleName,
              },
            }),
          )
        })
      }
    })
  }

  return data;
  }, [query.data]);
    

    return (
        <FormContextProvider initialValue={initialValue}>
            <FormContext.Consumer>
                {formContext => (
                <FormModal
                    modalProps={{
                        title: "Sign Acknowledgement Certification",
                    //   subTitle: (!fromWidget) ? `Compliance Certification for ${
                    //     (projectStore as any).projectName
                    //   }` : "",
                        submitBtnText: "Certify",
                        isExtraWide: true,
                    }}
                    onCancel={hideModal}
                    onSubmit={formData => {
                      return handleSubmit(formData, formContext)
                    }}
                >
                    <section className="bulk-cert-modal">
                        <div className="bulk-cert-modal__filter-signature">  
                          <span>Filter Signature :</span>
                          <select 
                            name="label-type"
                            onChange={(event) => {
                                setSelectedLabel(event.target.value)
                            }}
                          >
                            <option value="-10" hidden>Select Label</option>
                            <option value="ALL Signatures">ALL Signatures</option>
                            {
                              allLabels.length ? allLabels.map((ele) => {
                                return(
                                  <option value={ele}>{ele}</option>
                                )
                              }):
                              <option value="-10" disabled>No Labels Found</option>
                            }
                          </select>
                        </div>

                        <div className="bulk-cert-modal__form-schema-fields">
                          <FormSchemaFields
                          schema={schema}
                          onChange={formContext.set}
                          formData={formContext.value}
                          />
                        </div>

                        {error ?
                          <p className="error-text">
                            {
                              inspectionName.map(insp => (
                                <div>
                                  <strong>{insp}</strong>
                                </div>
                              ))
                            }
                            ** The following acknowledgement cannot be signed because it has pending inspection certifications to be signed.
                          </p>
                          :
                          null
                        }

                        {Array.isArray(query.data) ?
                        <>
                            <InspectionCertificationStatement
                                attestations={
                                    query.data[0].certState.attestations[SignatureGroupType.acknowledgement]
                                }
                            />
                            <SignatureCheckbox
                                enabled={
                                  !uploadBlock &&
                                  query.data[0].certState.groupOptions[
                                        SignatureGroupType.acknowledgement
                                    ].enabled
                                }
                                errors={[
                                  ...(query.data[0].certState.groupOptions[
                                        SignatureGroupType.acknowledgement
                                    ].errors || []),
                                  ...(uploadBlock ? [uploadBlock] : []),
                                ]}
                                checked={checked}
                                onSubmit={async () => {
                                    formContext.setError(null);
                                    setChecked(!checked);
                                }}
                            />
                        </>
                        : <></>
                        }
                    </section>
                </FormModal>
                )}
            </FormContext.Consumer>
        </FormContextProvider>
    );
}

export default BulkAcknowledgementCertifyModal